import { Card, CardContent } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Terms of Service | Woods Spine & Wellness"
        description="Terms of Service and Terms and Conditions for Woods Spine & Wellness."
        canonical="https://woodsspine.com/terms-service"
      />
      <Navbar />
      <main className="flex-grow">
        {/* Header Section */}
        <section className="bg-primary py-16 text-primary-foreground text-center">
          <div className="container mx-auto px-4">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Woods Spine & Wellness Terms of Service</h1>
            <p className="text-xl opacity-90">Last Updated: 01/01/2026</p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="shadow-lg border-0">
              <CardContent className="p-8 md:p-12 prose prose-slate max-w-none">
                <h2 className="text-2xl font-bold text-primary mb-6">Terms of Service / Terms and Conditions</h2>
                
                <p className="text-lg text-muted-foreground mb-8">
                  These Terms of Service (“Terms”) govern your use of the website and services provided by <strong>Woods Spine & Wellness</strong> (“we,” “us,” or “our”), including our website, appointment scheduling tools, SMS messaging programs, email communications, and related services.
                </p>
                <p className="text-lg text-muted-foreground mb-8">
                  By accessing or using our website or opting into our communications, you agree to be bound by these Terms and our Privacy Policy. If you do not agree, you should not use this website or our services.
                </p>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">1. Website Use & Medical Disclaimer</h3>
                <p className="mb-4">The content on this website is provided for <strong>informational purposes only</strong> and is <strong>not intended as a substitute for professional medical advice, diagnosis, or treatment</strong>.</p>
                <p className="mb-4">Always seek the advice of a qualified healthcare provider regarding any medical condition. Reliance on any information provided through this website is solely at your own risk.</p>
                <p className="mb-6">Use of this website does <strong>not</strong> establish a patient-provider relationship.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">2. Services & Communications Program</h3>
                <p className="mb-4">Woods Spine & Wellness may offer communications via <strong>SMS text messages and email</strong> to users who voluntarily opt in.</p>
                <p className="mb-4">Messages may include, but are not limited to:</p>
                <ul className="list-disc pl-6 mb-4 space-y-1">
                  <li>Appointment confirmations and reminders</li>
                  <li>Service-related notifications</li>
                  <li>Office updates or announcements</li>
                  <li>Promotional or educational messages</li>
                </ul>
                <p className="mb-6">Message frequency may vary.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">3. Consent to Receive Messages</h3>
                <p className="mb-4">By providing your phone number or email address and opting in, you expressly authorize Woods Spine & Wellness to send you SMS and/or email messages.</p>
                <ul className="list-disc pl-6 mb-6 space-y-1">
                  <li><strong>Consent is not a condition of purchase or treatment</strong></li>
                  <li>You may opt in through online forms, scheduling tools, or other clearly identified consent methods</li>
                </ul>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">4. Opt-Out Instructions</h3>
                <h4 className="text-lg font-semibold mb-2">SMS Opt-Out</h4>
                <p className="mb-4">You may cancel SMS messages at any time by replying <strong>STOP</strong> to any message. After sending STOP, you will receive a confirmation message and will no longer receive SMS messages unless you opt back in.</p>
                <h4 className="text-lg font-semibold mb-2">Email Opt-Out</h4>
                <p className="mb-6">You may unsubscribe from marketing emails at any time using the unsubscribe link included in the email.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">5. Help & Support</h3>
                <p className="mb-4">If you need assistance with our messaging program, reply <strong>HELP</strong> to any SMS message, or contact us directly at:</p>
                <ul className="list-none pl-0 mb-6 space-y-1">
                  <li><strong>Phone:</strong> (903) 215-8828</li>
                  <li><strong>Email:</strong> woodsspine.office@gmail.com</li>
                </ul>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">6. Message Frequency & Carrier Charges</h3>
                <p className="mb-6">Message frequency may vary. Message and data rates may apply depending on your wireless carrier and plan. Please contact your carrier for details regarding messaging or data charges.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">7. Carrier Disclaimer</h3>
                <p className="mb-6">Mobile carriers are <strong>not liable</strong> for delayed or undelivered messages.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">8. Privacy</h3>
                <p className="mb-6">Your use of this website and our communications programs is also governed by our <strong>Privacy Policy</strong>, which describes how we collect, use, and protect your information.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">9. Intellectual Property & Website Content</h3>
                <p className="mb-6">All content on this website, including text, graphics, logos, and layout, is the property of Woods Spine & Wellness unless otherwise stated. Unauthorized use or reproduction is prohibited.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">10. Disclaimer of Warranties</h3>
                <p className="mb-4">This website and its contents are provided on an <strong>“as is” and “as available”</strong> basis. We make no warranties, express or implied, regarding the accuracy, reliability, or availability of the website or its content.</p>
                <p className="mb-6">Your use of the website is at your sole risk.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">11. Limitation of Liability & Indemnification</h3>
                <p className="mb-4">To the fullest extent permitted by law, Woods Spine & Wellness shall not be liable for any damages arising from your use of this website or communications services.</p>
                <p className="mb-6">You agree to indemnify and hold harmless Woods Spine & Wellness, its owners, employees, and affiliates from any claims, damages, liabilities, or expenses arising from your use of the website or violation of these Terms.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">12. Governing Law</h3>
                <p className="mb-6">These Terms are governed by and construed in accordance with the laws of the <strong>State of Texas</strong>, without regard to conflict of law principles.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">13. Changes to These Terms</h3>
                <p className="mb-6">We may update these Terms from time to time. Updates will be posted on this page with a revised “Last Updated” date. Continued use of the website constitutes acceptance of the updated Terms.</p>

                <hr className="my-8 border-border" />

                <h3 className="text-xl font-bold text-primary mb-4">14. Contact Information</h3>
                <p className="mb-4">If you have questions about these Terms, please contact us:</p>
                <div className="bg-muted p-6 rounded-lg">
                  <p className="font-bold mb-2">Woods Spine & Wellness</p>
                  <p className="mb-1"><strong>Address:</strong> 530 E LOOP 281 STE 103, Longview, TX 75605</p>
                  <p className="mb-1"><strong>Phone:</strong> (903) 215-8828</p>
                  <p><strong>Email:</strong> woodsspine.office@gmail.com</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}